import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
	SdfAlert,
	SdfBusyIndicator,
	SdfButton,
	SdfExpandableBox,
	SdfFocusPane,
	SdfIcon,
	SdfLink,
	SdfSpotIllustration,
	SdfTooltip,
	SdfWizardLayout,
	SdfWizardStepsVerticalItem,
	SdfxWizardStepsVertical
} from "@synerg/react-components";
import { get } from 'lodash';
import CreateCertificateForm from './createCertificateForm';

function CreateCertificateModal({
	orgName,
	email,
	closeCreateCertModal,
	handleResponse,
	generateCert,
	submitReq
}) {
	const methods = useForm();
	const [stepNumber, setStepNumber] = useState(0);
	const [isBusy, setIsBusy] = useState(false);
	const [certificateData, setCertificateData] = useState({});
	const [certificate, setCertificate] = useState({});
	const [keyCopied, setKeyCopied] = useState(false);

	const instructionsUrl = 'https://developers.adp.com/articles/general/generate-a-certificate-signing-request';

	const onClickNextHandler = async () => {
		if (stepNumber === 2) {
			await submitRequest();
		}
		setStepNumber(stepNumber + 1);
	};

	const onClickBackHandler = () => {
		setStepNumber(stepNumber - 1);
	};

	const onGenerateKeys = async (formData) => {
		try {
			setIsBusy(true);
			const certData = {
				...formData,
				country: formData.countryObj.value
			}
			setCertificateData(certData);
			const response = await generateCert(certData);
			const cert = get(response, 'data', {});
			setCertificate(cert)
			setIsBusy(false);
			setKeyCopied(false);
			setStepNumber(stepNumber + 1);
		} catch (e) {
			setIsBusy(false);
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while generating keys. Please try again.');
			handleResponse(errorMessage);
		}
	};

	const submitRequest = async () => {
		setIsBusy(true);
		try {
			const requestData = Object.assign({}, certificateData);
			requestData.csr = certificate.csr;
			await submitReq(requestData);
			setIsBusy(false);
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while creating certificate. Please try again.');
			handleResponse(errorMessage);
			setIsBusy(false);
		}
	};

	return (
		<SdfFocusPane visible={true}
			paneType="floating"
			size="xl"
			hideAcceptButton={true}
			hideDismissButton={true}
			onSdfDismiss={() => { closeCreateCertModal(true); }}
			closeable={true}>
			<span slot="title" className="text-heading-07 font-black">Request new security certificate</span>
			<div className="p-10">
				<SdfWizardLayout>
					<SdfxWizardStepsVertical slot="wizard-steps" className="min-w-72">
						<SdfWizardStepsVerticalItem is-current={stepNumber === 0} step-status={stepNumber > 0 && "visited"}>
							Getting started
						</SdfWizardStepsVerticalItem>
						<SdfWizardStepsVerticalItem is-current={stepNumber === 1} step-status={stepNumber > 1 && "visited"}>
							Generate private key
						</SdfWizardStepsVerticalItem>
						<SdfWizardStepsVerticalItem is-current={stepNumber === 2} step-status={stepNumber > 2 && "visited"} >
							Copy/paste private key
						</SdfWizardStepsVerticalItem>
						<SdfWizardStepsVerticalItem is-current={stepNumber === 3} step-status={stepNumber > 3 && "visited"} >
							Generate certificate
						</SdfWizardStepsVerticalItem>
					</SdfxWizardStepsVertical>
					<div slot="page-content">
						{isBusy && <SdfBusyIndicator
							style={{ color: '#1a8099' }}
							size='lg'
							overlay={true}
							fullScreen={false}
						/>}
						{stepNumber === 0 && <div>
							<div className="text-heading-06 font-black"> Getting started</div>
							<div className="px-10 mt-3 flex flex-col justify-center bg-action-default-100 text-center rounded-lg" style={{ height: '500px' }}>
								<SdfSpotIllustration
									illustrationName={'browser-locked'}
									size={'md'}
								/>
								<div className="text-heading-06 font-black">Create your digital identity.</div>
								<p className="text-body-03">If your organization does not yet have a signed certificate from ADP's Certificate Authority (CA) or your existing certificate has expired,
									you may use this wizard to generate and submit a Certificate Signing Request (CSR) to ADP's CA for signing.
									If you prefer to create the CSR on your own, follow the instructions <SdfLink target="_blank" href={instructionsUrl}>here</SdfLink>.</p>
							</div>
						</div>}

						{stepNumber === 1 && <div>
							<div className="text-heading-06 font-black"> Generate private key</div>
							<div>
								<p className="font-bold"> Complete form to generate a certificate signing request (CSR). All fields are required.</p>
								<FormProvider {...methods} >
									<form>
										<CreateCertificateForm orgName={orgName} email={email} />
									</form>
								</FormProvider>
								<p className="text-tertiary mt-10">Your privacy is important to us; we will not use this information for marketing purposes.</p>
							</div>
						</div>}

						{stepNumber === 2 && <div>
							<div className="text-heading-06 font-black"> Copy/paste private key</div>
							<p>For security reasons, ADP can neither store nor retrieve this key for you at a later date. Please save to your computer now. You will need this key to access the ADP APIs.</p>
							<p>Please copy your private key (below) and paste inside a text editor on your computer. Save your private key using a .key extension (e.g. ADPMarketplaceIntegration.key).
								Treat your private key as confidential and make sure to safeguard it.</p>
							<div className="flex justify-between mt-5">
								<div className="text-lg font-bold"> Private key </div>
								<CopyToClipboard text={certificate.key} onCopy={() => setKeyCopied(true)}>
									<SdfTooltip positionContext="ELEMENT" trigger="click" state="info" attachmentPoint="top">
										<SdfButton variant="text" emphasis="secondary" slot="tooltip-target">
											<SdfIcon icon="action-copy" /> Copy
										</SdfButton>
										Copied
									</SdfTooltip>
								</CopyToClipboard>
							</div>
							<div className="p-5 whitespace-pre-wrap bg-action-default-100 rounded-md mt-2">
								{certificate.key}
							</div>
						</div>}

						{stepNumber === 3 && <div>
							<div className="text-heading-06 font-black"> Generate Certificate</div>
							<SdfAlert
								status='success'
								size='sm'
								aria-live="polite"
								closeable={true}
								className="sm:col-span-2 md:col-span-1 break-all mt-5"
							>
								<span slot="title" className="text-md font-normal">Your certificate is being generated. Please check the status on the certificate page and click download.</span>
							</SdfAlert>
							<SdfExpandableBox
								variant="bordered"
								triggerPlacement="after"
								opened={true}
								header={"Certificate Signing Request (CSR)"}
								headingLevel="3"
								className="mt-10"
							>
								<div className="whitespace-pre-wrap mt-5">
									{certificate.csr}
								</div>
							</SdfExpandableBox>
						</div>}
					</div>
				</SdfWizardLayout>
			</div>
			<div slot="custom-buttons">
				{stepNumber < 3 &&
					<SdfButton
						variant="text"
						action="standard"
						active={false}
						onClick={() => { closeCreateCertModal(false) }}
					>
						Cancel
					</SdfButton>}
				{stepNumber > 0 && stepNumber < 3 &&
					<SdfButton
						icon="nav-move-left"
						iconPlacement="before"
						emphasis="secondary"
						className="ml-5"
						onClick={() => {
							onClickBackHandler();
						}}
					>
						Back
					</SdfButton>}

				{stepNumber === 0 &&
					<SdfButton
						icon="nav-move-right"
						iconPlacement="after"
						emphasis="primary"
						className="ml-5"
						onClick={() => {
							onClickNextHandler();
						}}
					>
						Next
					</SdfButton>}

				{stepNumber === 1 &&
					<SdfButton
						icon="nav-move-right"
						iconPlacement="after"
						emphasis="primary"
						className="ml-5"
						onClick={methods.handleSubmit(d => { onGenerateKeys(d) })}
					>
						Next
					</SdfButton>}

				{stepNumber === 2 && <div>
					{!keyCopied ? <SdfTooltip positionContext="ELEMENT" trigger="hover" state="info" attachmentPoint="top">
						<SdfButton
							slot="tooltip-target"
							icon="nav-move-right"
							iconPlacement="after"
							emphasis="primary"
							className="ml-5"
							disabled={true}
						>
							NEXT
						</SdfButton>
						Please copy the key before clicking on NEXT
					</SdfTooltip> :
						<SdfButton
							slot="tooltip-target"
							icon="nav-move-right"
							iconPlacement="after"
							emphasis="primary"
							className="ml-5"
							{...(!keyCopied && { disabled: true })}
							onClick={() => {
								onClickNextHandler();
							}}
						>
							NEXT
						</SdfButton>}
				</div>}

				{stepNumber === 3 &&
					<SdfButton
						emphasis="primary"
						className="ml-5"
						onClick={() => {
							closeCreateCertModal(true);
						}}
					>
						Done
					</SdfButton>}
			</div>
		</SdfFocusPane>
	)
};

export default CreateCertificateModal;
